// ABOUTME: The aerial wa'a of the 5D summary: the design bundle's animated canoe drawing, with sails and wakes that can be driven.
// ABOUTME: Sails unfold and wakes fade in on the send-off; the wake loops stop under reduced motion.

import { useId } from 'react';
import type { CSSProperties } from 'react';
import { styled } from '@mui/material/styles';

// A styled svg rather than a Box so width and height stay attributes, as they are in the asset.
const CanoeSvg = styled('svg')({
  '@keyframes bowFlowLong': { from: { strokeDashoffset: 0 }, to: { strokeDashoffset: -197 } },
  '@keyframes rippleFlow': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(-14px)' } },
  '&[data-flow="on"] .bow-flow': { animation: 'bowFlowLong 15.5s linear infinite' },
  '&[data-flow="on"] .ripple-flow': { animation: 'rippleFlow 0.7s linear infinite' },
});

const SAIL_MAST_LEAN = '10.1deg';
const sailSqueeze = (scaleX: number) => `rotate(${SAIL_MAST_LEAN}) matrix(${scaleX},0,0,1,0,0) rotate(-${SAIL_MAST_LEAN})`;

interface AerialWaaProps {
  unfurled: boolean;
  wakes: boolean;
  reducedMotion: boolean;
}

export function AerialWaa({ unfurled, wakes, reducedMotion }: AerialWaaProps) {
  const prefix = useId().replace(/:/g, '');
  const ids = {
    hullShadow: `${prefix}-hullShadow`,
    rippleFade: `${prefix}-rippleFade`,
    rippleMask: `${prefix}-rippleMask`,
    bowWakeC: `${prefix}-bowWakeC`,
    fwdWakeC: `${prefix}-fwdWakeC`,
    midWakeC: `${prefix}-midWakeC`,
  };
  const sailStyle: CSSProperties = {
    transformBox: 'fill-box',
    transformOrigin: '77.5% 100%',
    transform: sailSqueeze(unfurled ? 1 : 0.08),
    transition: 'transform 1.4s cubic-bezier(.55,.05,.45,1) .2s',
  };
  const wakeStyle = (delaySeconds: number): CSSProperties => ({
    opacity: wakes ? 1 : 0,
    transition: `opacity 1.2s ease${delaySeconds > 0 ? ` ${delaySeconds}s` : ''}`,
  });

  return (
    <CanoeSvg
      viewBox="-215 -40 450 200"
      width="450"
      height="200"
      overflow="visible"
      aria-hidden="true"
      data-testid="aerial-waa"
      data-flow={reducedMotion ? 'off' : 'on'}
    >
      <defs>
        <filter id={ids.hullShadow} x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>
      <ellipse cx="112" cy="117.5" rx="100" ry="13" fill="rgba(30,70,85,0.28)" filter={`url(#${ids.hullShadow})`} />
      <ellipse cx="114" cy="48.5" rx="100" ry="13" fill="rgba(30,70,85,0.28)" filter={`url(#${ids.hullShadow})`} />
      <defs>
        <linearGradient id={ids.rippleFade} gradientUnits="userSpaceOnUse" x1="-12" y1="0" x2="-182" y2="0">
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={ids.rippleMask}>
          <rect x="-190" y="60" width="180" height="24" fill={`url(#${ids.rippleFade})`} />
        </mask>
      </defs>
      <g mask={`url(#${ids.rippleMask})`} data-wake style={wakeStyle(0)}>
        <g className="ripple-flow">
          <rect x="-1" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="0.6000000000000001" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-15" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-13.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-29" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-27.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-43" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-41.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-57" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-55.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-71" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-69.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-85" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-83.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-99" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-97.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-113" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-111.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-127" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-125.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-141" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-139.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-155" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-153.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-169" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-167.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-183" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-181.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
          <rect x="-197" y="67.5" width="3" height="9" fill="rgba(96,150,170,0.48)" />
          <rect x="-195.4" y="70.1" width="1.6" height="3.8" fill="rgba(255,255,255,0.8)" />
        </g>
      </g>
      <g strokeLinecap="round" fill="none">
        <path d="M30 72 L-2 72" stroke="#B08A55" strokeWidth="3" />
        <path d="M0 72 L-24 72" stroke="#8E6B46" strokeWidth="7" />
      </g>
      <defs>
        <linearGradient id={ids.bowWakeC} gradientUnits="userSpaceOnUse" x1="110" y1="0" x2="-55" y2="0">
          <stop offset="0" stopColor="rgba(86,142,165,0.45)" />
          <stop offset="0.3" stopColor="rgba(175,210,218,0.7)" />
          <stop offset="0.72" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="1" stopColor="rgba(255,255,255,0.55)" />
        </linearGradient>
        <linearGradient id={ids.fwdWakeC} gradientUnits="userSpaceOnUse" x1="226" y1="0" x2="10" y2="0">
          <stop offset="0" stopColor="rgba(86,142,165,0.45)" />
          <stop offset="0.3" stopColor="rgba(175,210,218,0.7)" />
          <stop offset="0.65" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <linearGradient id={ids.midWakeC} gradientUnits="userSpaceOnUse" x1="-6" y1="0" x2="-205" y2="0">
          <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="0.4" stopColor="rgba(255,255,255,0.9)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
      <g fill="none" strokeLinecap="round" data-wake style={wakeStyle(0.2)}>
        <path d="M226 32 C170 24 90 12 10 -4" stroke={`url(#${ids.fwdWakeC})`} strokeWidth="1.5" strokeDasharray="26 3 38 3 55 3 69 3" className="bow-flow" />
        <path d="M226 112 C170 120 90 132 10 148" stroke={`url(#${ids.fwdWakeC})`} strokeWidth="1.5" strokeDasharray="26 3 38 3 55 3 69 3" className="bow-flow" />
        <path d="M110 22 C65 18.7 10 13 -55 6.7" stroke={`url(#${ids.bowWakeC})`} strokeWidth="1.5" strokeDasharray="26 3 38 3 55 3 69 3" className="bow-flow" />
        <path d="M-6 36 C-60 35.5 -130 33 -205 30" stroke={`url(#${ids.midWakeC})`} strokeWidth="1" strokeDasharray="22 3 41 3 60 3 62 3" className="bow-flow" />
        <path d="M110 122 C65 125.3 10 131 -55 137.3" stroke={`url(#${ids.bowWakeC})`} strokeWidth="1.5" strokeDasharray="26 3 38 3 55 3 69 3" className="bow-flow" />
        <path d="M-6 108 C-60 108.5 -130 111 -205 114" stroke={`url(#${ids.midWakeC})`} strokeWidth="1" strokeDasharray="22 3 41 3 60 3 62 3" className="bow-flow" />
      </g>
      <path d="M10 37.5 Q10 25.5 24 25.5 L196 25.5 Q210 25.5 210 37.5 Q210 49.5 196 49.5 L24 49.5 Q10 49.5 10 37.5 Z" fill="#5B3A2C" stroke="#3E2419" strokeWidth="0.8" />
      <path d="M208 32.5 Q222 31.5 228 37.5 Q222 43.5 208 42.5 Z" fill="#7A553F" stroke="#3E2419" strokeWidth="0.8" strokeLinejoin="round" />
      <path d="M12 32.5 Q-2 31.5 -8 37.5 Q-2 43.5 12 42.5 Z" fill="#7A553F" stroke="#3E2419" strokeWidth="0.8" strokeLinejoin="round" />
      <path d="M10 106.5 Q10 94.5 24 94.5 L196 94.5 Q210 94.5 210 106.5 Q210 118.5 196 118.5 L24 118.5 Q10 118.5 10 106.5 Z" fill="#5B3A2C" stroke="#3E2419" strokeWidth="0.8" />
      <path d="M208 101.5 Q222 100.5 228 106.5 Q222 112.5 208 111.5 Z" fill="#7A553F" stroke="#3E2419" strokeWidth="0.8" strokeLinejoin="round" />
      <path d="M12 101.5 Q-2 100.5 -8 106.5 Q-2 112.5 12 111.5 Z" fill="#7A553F" stroke="#3E2419" strokeWidth="0.8" strokeLinejoin="round" />
      <g fill="#BFA078">
        <rect x="22.5" y="26.5" width="3" height="91" rx="1.5" />
        <rect x="55.5" y="26.5" width="3" height="91" rx="1.5" />
        <rect x="88.5" y="19.5" width="3" height="105" />
        <rect x="121.5" y="19.5" width="3" height="105" />
        <rect x="154.5" y="19.5" width="3" height="105" />
      </g>
      <rect x="38" y="30.5" width="120" height="83" fill="#EADFC8" />
      <g stroke="#D6C29C" strokeWidth="0.8">
        <line x1="38" y1="38.5" x2="158" y2="38.5" />
        <line x1="38" y1="46.5" x2="158" y2="46.5" />
        <line x1="38" y1="54.5" x2="158" y2="54.5" />
        <line x1="38" y1="62.5" x2="158" y2="62.5" />
        <line x1="38" y1="70.5" x2="158" y2="70.5" />
        <line x1="38" y1="78.5" x2="158" y2="78.5" />
        <line x1="38" y1="86.5" x2="158" y2="86.5" />
        <line x1="38" y1="94.5" x2="158" y2="94.5" />
        <line x1="38" y1="102.5" x2="158" y2="102.5" />
        <line x1="38" y1="110.5" x2="158" y2="110.5" />
      </g>
      <rect x="10" y="32.5" width="28" height="79" fill="#3A4454" stroke="#8E6B46" strokeWidth="1" />
      <g stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="0.6">
        <line x1="17" y1="32.5" x2="17" y2="111.5" />
        <line x1="24" y1="32.5" x2="24" y2="111.5" />
        <line x1="31" y1="32.5" x2="31" y2="111.5" />
        <line x1="10" y1="44.5" x2="38" y2="44.5" />
        <line x1="10" y1="56.5" x2="38" y2="56.5" />
        <line x1="10" y1="68.5" x2="38" y2="68.5" />
        <line x1="10" y1="80.5" x2="38" y2="80.5" />
        <line x1="10" y1="92.5" x2="38" y2="92.5" />
        <line x1="10" y1="104.5" x2="38" y2="104.5" />
      </g>
      <rect x="160" y="30.5" width="48" height="83" fill="#EADFC8" fillOpacity="0.35" />
      <g stroke="#FFFFFF" strokeOpacity="0.7" strokeWidth="0.6">
        <line x1="166" y1="30.5" x2="166" y2="113.5" />
        <line x1="173" y1="30.5" x2="173" y2="113.5" />
        <line x1="180" y1="30.5" x2="180" y2="113.5" />
        <line x1="187" y1="30.5" x2="187" y2="113.5" />
        <line x1="194" y1="30.5" x2="194" y2="113.5" />
        <line x1="201" y1="30.5" x2="201" y2="113.5" />
        <line x1="160" y1="37.5" x2="208" y2="37.5" />
        <line x1="160" y1="44.5" x2="208" y2="44.5" />
        <line x1="160" y1="51.5" x2="208" y2="51.5" />
        <line x1="160" y1="58.5" x2="208" y2="58.5" />
        <line x1="160" y1="65.5" x2="208" y2="65.5" />
        <line x1="160" y1="72.5" x2="208" y2="72.5" />
        <line x1="160" y1="79.5" x2="208" y2="79.5" />
        <line x1="160" y1="86.5" x2="208" y2="86.5" />
        <line x1="160" y1="93.5" x2="208" y2="93.5" />
        <line x1="160" y1="100.5" x2="208" y2="100.5" />
        <line x1="160" y1="107.5" x2="208" y2="107.5" />
      </g>
      <rect x="160" y="58.2" width="46" height="27.7" fill="#EADFC8" stroke="#C9A574" strokeWidth="0.8" />
      <g stroke="#D6C29C" strokeWidth="0.8">
        <line x1="160" y1="67.4" x2="206" y2="67.4" />
        <line x1="160" y1="76.7" x2="206" y2="76.7" />
      </g>
      <rect x="156.5" y="29.5" width="3.5" height="85" fill="#C9A574" stroke="#8E6B46" strokeWidth="0.6" />
      <g stroke="#8E6B46" strokeWidth="1.2">
        <line x1="38" y1="31.5" x2="160" y2="31.5" />
        <line x1="38" y1="112.5" x2="160" y2="112.5" />
      </g>
      <g fill="#EADFC8" stroke="#8E6B46" strokeWidth="0.4">
        <rect x="37" y="30.2" width="2.6" height="2.6" />
        <rect x="37" y="111.2" width="2.6" height="2.6" />
        <rect x="55.7" y="30.2" width="2.6" height="2.6" />
        <rect x="55.7" y="111.2" width="2.6" height="2.6" />
        <rect x="88.7" y="30.2" width="2.6" height="2.6" />
        <rect x="88.7" y="111.2" width="2.6" height="2.6" />
        <rect x="121.7" y="30.2" width="2.6" height="2.6" />
        <rect x="121.7" y="111.2" width="2.6" height="2.6" />
      </g>
      <rect x="198" y="19.5" width="4" height="105" fill="#C9A574" stroke="#5B3A2C" strokeWidth="0.5" />
      <rect x="170" y="19.5" width="4" height="105" fill="#C9A574" stroke="#5B3A2C" strokeWidth="0.5" />
      <g fill="#EADFC8" stroke="#8E6B46" strokeWidth="0.4">
        <rect x="21.5" y="24" width="5" height="7" rx="1.5" />
        <rect x="21.5" y="113" width="5" height="7" rx="1.5" />
        <rect x="54.5" y="24" width="5" height="7" rx="1.5" />
        <rect x="54.5" y="113" width="5" height="7" rx="1.5" />
        <rect x="87.5" y="24" width="5" height="7" rx="1.5" />
        <rect x="87.5" y="113" width="5" height="7" rx="1.5" />
        <rect x="120.5" y="24" width="5" height="7" rx="1.5" />
        <rect x="120.5" y="113" width="5" height="7" rx="1.5" />
        <rect x="153.5" y="24" width="5" height="7" rx="1.5" />
        <rect x="153.5" y="113" width="5" height="7" rx="1.5" />
        <rect x="197.5" y="24" width="5" height="7" rx="0.5" />
        <rect x="197.5" y="113" width="5" height="7" rx="0.5" />
        <rect x="169.5" y="24" width="5" height="7" rx="0.5" />
        <rect x="169.5" y="113" width="5" height="7" rx="0.5" />
      </g>
      <rect x="40" y="118.5" width="31" height="9.5" fill="#2F4A6B" stroke="#C9A574" strokeWidth="0.8" />
      <g stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="0.5">
        <line x1="40" y1="121.7" x2="71" y2="121.7" />
        <line x1="40" y1="124.8" x2="71" y2="124.8" />
      </g>
      <rect x="40" y="125" width="31" height="3" fill="#EADFC8" fillOpacity="0.3" />
      <g stroke="#FFFFFF" strokeOpacity="0.75" strokeWidth="0.4">
        <line x1="42.5" y1="125" x2="42.5" y2="128" />
        <line x1="45" y1="125" x2="45" y2="128" />
        <line x1="47.5" y1="125" x2="47.5" y2="128" />
        <line x1="50" y1="125" x2="50" y2="128" />
        <line x1="52.5" y1="125" x2="52.5" y2="128" />
        <line x1="55" y1="125" x2="55" y2="128" />
        <line x1="57.5" y1="125" x2="57.5" y2="128" />
        <line x1="60" y1="125" x2="60" y2="128" />
        <line x1="62.5" y1="125" x2="62.5" y2="128" />
        <line x1="65" y1="125" x2="65" y2="128" />
        <line x1="67.5" y1="125" x2="67.5" y2="128" />
        <line x1="70" y1="125" x2="70" y2="128" />
        <line x1="40" y1="126.5" x2="71" y2="126.5" />
      </g>
      <line x1="40" y1="128" x2="71" y2="128" stroke="#8E6B46" strokeWidth="1" />
      <g fill="#F6F1E4" stroke="#8E6B46" strokeWidth="0.4">
        <rect x="38.6" y="117.1" width="2.8" height="2.8" rx="0.4" />
        <rect x="69.6" y="117.1" width="2.8" height="2.8" rx="0.4" />
        <rect x="38.6" y="126.6" width="2.8" height="2.8" rx="0.4" />
        <rect x="69.6" y="126.6" width="2.8" height="2.8" rx="0.4" />
      </g>
      <g stroke="#5B3A2C" strokeOpacity="0.55" strokeWidth="0.7" strokeLinecap="round" fill="none">
        <line x1="172.6" y1="-32.1" x2="156" y2="21" />
        <line x1="120.6" y1="-32.1" x2="102" y2="38" />
        <line x1="120.6" y1="-32.1" x2="90" y2="21" />
        <line x1="120.6" y1="-32.1" x2="123" y2="21" />
      </g>
      <g fill="#F6F1E4" stroke="#8E6B46" strokeWidth="0.4">
        <rect x="154.7" y="19.7" width="2.6" height="2.6" />
        <rect x="121.7" y="19.7" width="2.6" height="2.6" />
        <rect x="88.7" y="19.7" width="2.6" height="2.6" />
      </g>
      <g fill="#8E6B46" stroke="#5B3A2C" strokeWidth="0.6">
        <rect x="48" y="32" width="108.5" height="5" />
        <rect x="48" y="107" width="108.5" height="5" />
      </g>
      <g style={{ filter: 'drop-shadow(3px 4px 3px rgba(0,0,0,0.28))' }}>
        <path d="M102 72 Q117.2 17.9 120.6 -32.1 Q84.2 28.7 37.8 -13.2 Q56.4 34.2 102 72 Z" fill="#C97A5C" stroke="#8E4A33" strokeWidth="0.8" strokeLinejoin="round" style={sailStyle} />
        <g stroke="#5B3A2C" strokeOpacity="0.55" strokeWidth="0.7" strokeLinecap="round" fill="none" style={{ filter: 'none' }}>
          <line x1="120.6" y1="-32.1" x2="123" y2="123" />
          <line x1="120.6" y1="-32.1" x2="90" y2="123" />
        </g>
        <path d="M154 72 Q169.2 17.9 172.6 -32.1 Q136.2 28.7 89.8 -13.2 Q108.4 34.2 154 72 Z" fill="#B9664A" stroke="#8E4A33" strokeWidth="0.8" strokeLinejoin="round" style={sailStyle} />
      </g>
      <g stroke="#5B3A2C" strokeOpacity="0.55" strokeWidth="0.7" strokeLinecap="round" fill="none">
        <line x1="172.6" y1="-32.1" x2="200" y2="21" />
        <line x1="172.6" y1="-32.1" x2="200" y2="33" />
        <line x1="172.6" y1="-32.1" x2="200" y2="111" />
        <line x1="172.6" y1="-32.1" x2="200" y2="123" />
        <line x1="172.6" y1="-32.1" x2="156" y2="123" />
      </g>
      <g fill="#F6F1E4" stroke="#8E6B46" strokeWidth="0.4">
        <rect x="198.7" y="19.7" width="2.6" height="2.6" />
        <rect x="198.7" y="31.7" width="2.6" height="2.6" />
        <rect x="198.7" y="109.7" width="2.6" height="2.6" />
        <rect x="198.7" y="121.7" width="2.6" height="2.6" />
        <rect x="154.7" y="121.7" width="2.6" height="2.6" />
        <rect x="121.7" y="121.7" width="2.6" height="2.6" />
        <rect x="88.7" y="121.7" width="2.6" height="2.6" />
      </g>
      <circle cx="154" cy="72" r="3.5" fill="#5B3A2C" />
      <circle cx="154" cy="72" r="1.2" fill="#B08A55" />
      <circle cx="102" cy="72" r="3.5" fill="#5B3A2C" />
      <circle cx="102" cy="72" r="1.2" fill="#B08A55" />
    </CanoeSvg>
  );
}
